import {useEffect, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useWorkspace} from '../hooks/useWorkspace';
import {dataClient} from '../data/dataClient';
import {sendChat} from '../services/api';
import ApprovalGate from '../components/ApprovalGate';

const {FiMail, FiSparkles, FiCheck, FiSend, FiEdit3, FiRefreshCw} = FiIcons;

const REFINE_PRESETS = [
  'Make this more formal and executive',
  'Make this concise and direct',
  'Add warmth and emphasize our brand principles',
  'Clarify production lead times and next steps'
];

export default function Inbox() {
  const state = useWorkspace();
  const inbox = state.inbox || state.inboxMessages || [];

  const [selectedId, setSelectedId] = useState(inbox[0]?.id || null);
  const [draftText, setDraftText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState('');
  const [refining, setRefining] = useState(false);
  const [statusNotice, setStatusNotice] = useState(null);

  const selected = inbox.find((message) => message.id === selectedId) || inbox[0] || null;

  // Resolve inbox bot (Milo)
  const inboxBot = state.bots?.find((b) => b.slug === 'inbox-bot')
    || state.bots?.find((b) => b.module === 'inbox')
    || state.bots?.find((b) => b.isLead && b.enabled);

  useEffect(() => {
    if (!selectedId && inbox.length) {
      setSelectedId(inbox[0].id);
    }
  }, [inbox, selectedId]);

  useEffect(() => {
    if (selected) {
      setDraftText(selected.draft || '');
      setIsEditing(false);
      setStatusNotice(null);
    }
  }, [selected?.id]);

  async function handleSaveDraft() {
    if (!selected) return;
    await dataClient.update('inboxMessages', selected.id, {draft: draftText});
    setIsEditing(false);
    setStatusNotice('Draft saved.');
    setTimeout(() => setStatusNotice(null), 2500);
  }

  async function handleApproveAndSend() {
    if (!selected) return;
    await dataClient.update('inboxMessages', selected.id, {
      status: 'sent',
      draft: draftText,
      sentAt: new Date().toISOString()
    });
    setStatusNotice(`Reply approved and sent to ${selected.from}!`);
    setTimeout(() => setStatusNotice(null), 4000);
  }

  async function handleRefine(instruction) {
    if (!selected || !instruction.trim() || refining) return;
    setRefining(true);
    setStatusNotice('Milo is refining the draft…');

    try {
      const prompt = `Refine this drafted email reply to ${selected.from}. Instruction: "${instruction}". Inbound inquiry: "${selected.body || selected.preview}". Current draft: "${draftText}". Provide only the improved reply.`;
      const reply = await sendChat({
        module: 'inbox',
        botId: inboxBot?.id || 'bot_bm',
        text: prompt,
        sandbox: true
      });

      if (reply?.text) {
        // Strip out any debug labels if present
        const cleaned = reply.text.replace(/^\[dev mock\]\s*/i, '');
        setDraftText(cleaned);
        await dataClient.update('inboxMessages', selected.id, {draft: cleaned});
        setStatusNotice('Draft refined by Milo.');
      }
    } catch (err) {
      setStatusNotice(`Refine failed: ${err.message}`);
    } finally {
      setRefining(false);
      setRefinePrompt('');
      setTimeout(() => setStatusNotice(null), 3500);
    }
  }

  if (!inbox.length) {
    return (
      <div className="page">
        <header className="page-header">
          <span className="eyebrow">Inbox</span>
          <h1>Every message, already sorted.</h1>
          <p>Review triage and bot-drafted replies before anything is sent.</p>
        </header>
        <section className="panel">
          <span className="eyebrow">Inbox clear</span>
          <h2>No messages need review.</h2>
          <p>Your inbox will appear here when messages are available.</p>
        </section>
      </div>
    );
  }

  const isSent = selected?.status === 'sent';

  return (
    <div className="page">
      <header className="page-header">
        <span className="eyebrow">Inbox</span>
        <h1>Every message, already sorted.</h1>
        <p>Review triage, refine responses with Milo, and approve replies before anything is sent.</p>
      </header>

      <div className="inbox-grid">
        {/* Left: Message List */}
        <section className="panel inbox-list">
          {inbox.map((message) => (
            <button
              className={selected?.id === message.id ? 'active' : ''}
              key={message.id}
              onClick={() => setSelectedId(message.id)}
            >
              <span className="file-icon">
                <SafeIcon icon={FiMail} />
              </span>
              <span>
                <b>{message.subject}</b>
                <small>{message.from}</small>
                <p>{message.preview}</p>
              </span>
              <span className={`status ${message.status || 'new'}`}>
                {message.status || 'new'}
              </span>
            </button>
          ))}
        </section>

        {/* Right: Message Detail & Reply Composer */}
        {selected && (
          <section className="panel reply-preview">
            <div className="inline spread">
              <span className="eyebrow">From {selected.from}</span>
              <span className={`status ${selected.status || 'needs-reply'}`}>
                {selected.status || 'needs-reply'}
              </span>
            </div>

            <h2>{selected.subject}</h2>
            <p style={{whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'var(--color-inkSoft)', marginTop: '8px'}}>
              {selected.body || selected.preview}
            </p>

            <hr />

            {/* Status notice */}
            {statusNotice && (
              <div className="success-note" style={{marginBottom: '14px', marginTop: 0}}>
                {statusNotice}
              </div>
            )}

            <div className="inline spread">
              <div className="draft-label">
                <SafeIcon icon={FiSparkles} />
                <b>Bot-drafted reply (by Milo)</b>
              </div>
              {!isSent && (
                <button
                  type="button"
                  className="button ghost compact"
                  onClick={() => {
                    if (isEditing) handleSaveDraft();
                    else setIsEditing(true);
                  }}
                >
                  <SafeIcon icon={isEditing ? FiCheck : FiEdit3} />
                  {isEditing ? 'Save draft' : 'Edit draft'}
                </button>
              )}
            </div>

            <textarea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              readOnly={!isEditing && !isSent}
              style={{
                background: isEditing ? 'white' : 'var(--color-surfaceAlt)',
                borderColor: isEditing ? varColorBlue : 'var(--color-line)'
              }}
              placeholder="Draft your reply here…"
            />

            {/* Refine with Bot toolbar */}
            {!isSent && (
              <div className="inbox-refine-block" style={{marginTop: '12px', padding: '14px', background: 'var(--color-surfaceAlt)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-line)'}}>
                <span className="eyebrow" style={{fontSize: '11px', display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px'}}>
                  <SafeIcon icon={FiRefreshCw} /> Refine reply with Milo
                </span>

                <div className="tag-chips" style={{marginBottom: '10px'}}>
                  {REFINE_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      className="chip"
                      onClick={() => handleRefine(preset)}
                      disabled={refining}
                    >
                      {preset}
                    </button>
                  ))}
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleRefine(refinePrompt);
                  }}
                  className="inline"
                  style={{gap: '8px'}}
                >
                  <input
                    value={refinePrompt}
                    onChange={(e) => setRefinePrompt(e.target.value)}
                    placeholder="e.g. “Add a warm sign-off and mention our catalog link”"
                    disabled={refining}
                    style={{flex: 1}}
                  />
                  <button type="submit" className="button secondary compact" disabled={refining || !refinePrompt.trim()}>
                    {refining ? 'Refining…' : 'Refine'}
                  </button>
                </form>
              </div>
            )}

            {/* Approval Gate */}
            <div style={{marginTop: '16px'}}>
              <ApprovalGate
                status={isSent ? 'approved' : 'draft'}
                onApprove={handleApproveAndSend}
                onRevoke={isSent ? () => dataClient.update('inboxMessages', selected.id, {status: 'needs-reply'}) : null}
                title={isSent ? 'Reply sent' : 'Explicit send approval'}
                description={
                  isSent
                    ? `Sent to ${selected.from}.`
                    : 'Nothing is sent until you explicitly approve and dispatch this reply.'
                }
                approveLabel="Approve & Send"
                revokeLabel="Re-open to Draft"
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const varColorBlue = 'var(--color-blue, #3b82f6)';
